import { MutableRefObject, useCallback, useEffect } from 'react';
import { getMFEConfig } from '@sunrun/sales-experience-shared';
import { publishMessage } from 'helpers/messaging';
import type { Message } from 'helpers/messaging';

type AnalyticsMessage = {
  type: string;
  source: string;
  payload?: Record<string, unknown>;
};

const ANALYTICS_CONTAINER_PATH = getMFEConfig(
  'analytics',
  'analytics',
).containerPath;

const FORWARDED_COMMAND_TYPES = new Set([
  'COMMAND:CHANGE_URL',
  'COMMAND:CREATE_LEAD',
  'COMMAND:LOGIN_REDIRECT',
]);

const postMessageToIframe = (
  ref: MutableRefObject<HTMLIFrameElement | null>,
  message: AnalyticsMessage,
) => {
  ref.current?.contentWindow?.postMessage(message, '*');
};

const useAnalyticsIframeMessages = (
  ref: MutableRefObject<HTMLIFrameElement | null>,
) => {
  const sendEventBootstrap = useCallback(() => {
    postMessageToIframe(ref, {
      type: 'EVENT:BOOTSTRAP',
      source: 'Container',
      payload: {
        baseUrl: `${window.location.origin}${ANALYTICS_CONTAINER_PATH}`,
      },
    });
  }, [ref]);

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      const message = event.data as AnalyticsMessage | undefined;
      if (!message || message.source !== 'Analytics') {
        return;
      }

      if (message.type === 'EVENT:READY') {
        sendEventBootstrap();
        return;
      }

      if (FORWARDED_COMMAND_TYPES.has(message.type)) {
        publishMessage({ ...message, source: 'Dashboard' } as Message);
      }
    };

    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [sendEventBootstrap]);
};

export { useAnalyticsIframeMessages };
